// Legacy → graph adapter. Projects the existing hand-authored content
// (NIST/ISO controls, ATT&CK techniques, the NIST↔ISO crosswalk) into the
// Entity/Relationship model with NO loss, so the current UI renders identically
// while the app moves onto registry-driven selectors. Phase 2+ replaces these
// sources with generated entity data transformed from the authoritative datasets.

import {
  Entity,
  Relationship,
  DomainId,
  validateEntities,
  validateRelationships,
} from '../schema';

// Legacy JS data modules (untyped) — imported via allowJs.
// @ts-ignore
import { nistControlsByDomain, nistControlDetails } from '../controls/nist-controls';
// @ts-ignore
import { isoControlsByDomain, isoControlDetails } from '../controls/iso-controls';
// @ts-ignore
import { domainTTPs } from '../mitre/attack-mappings';
// @ts-ignore
import { techniqueDetails } from '../mitre/technique-details';
// @ts-ignore
import { controlMappings } from '../../utils/controlMapper';

type ByDomain = Record<string, Record<string, { controls?: string[] } | string[]>>;

/** Map each control/technique id → the set of domains it is surfaced under. */
function domainsByControl(byDomain: ByDomain): Map<string, Set<DomainId>> {
  const map = new Map<string, Set<DomainId>>();
  for (const [domain, categories] of Object.entries(byDomain)) {
    for (const category of Object.values(categories)) {
      const ids = Array.isArray(category) ? category : category?.controls ?? [];
      for (const id of ids) {
        if (!map.has(id)) map.set(id, new Set());
        map.get(id)!.add(domain as DomainId);
      }
    }
  }
  return map;
}

function domainsByTechnique(): Map<string, Set<DomainId>> {
  const map = new Map<string, Set<DomainId>>();
  for (const [domain, ids] of Object.entries(domainTTPs as Record<string, string[]>)) {
    for (const id of ids) {
      if (!map.has(id)) map.set(id, new Set());
      map.get(id)!.add(domain as DomainId);
    }
  }
  return map;
}

function controlEntities(
  frameworkId: string,
  details: Record<string, any>,
  byDomain: ByDomain,
): Entity[] {
  const domMap = domainsByControl(byDomain);
  return Object.entries(details).map(([id, meta]) => ({
    id,
    frameworkId,
    kind: 'control' as const,
    name: meta?.name ?? id,
    domains: Array.from(domMap.get(id) ?? []),
    tags: [],
    metadata: meta ?? {},
  }));
}

function techniqueEntities(): Entity[] {
  const domMap = domainsByTechnique();
  return Object.entries(techniqueDetails as Record<string, any>).map(([id, meta]) => ({
    id,
    frameworkId: 'mitre-attack',
    kind: 'offensive-technique' as const,
    name: meta?.technique_name ?? id,
    domains: Array.from(domMap.get(id) ?? []),
    tags: [],
    metadata: meta ?? {},
  }));
}

const entities: Entity[] = [
  ...controlEntities('nist-800-53-r5', nistControlDetails, nistControlsByDomain),
  ...controlEntities('iso-27001-2022', isoControlDetails, isoControlsByDomain),
  ...techniqueEntities(),
];

const knownIds = new Set(entities.map((e) => e.id));

/** Build NIST↔ISO `maps-to` relationships from the hand-curated crosswalk.
 * Only keep links whose BOTH endpoints are known entities, so the graph stays
 * referentially intact. (Compare still uses the raw crosswalk util directly, so
 * its behaviour is unchanged even where a target lacks detail content.) */
function mapsToRelationships(): Relationship[] {
  const out: Relationship[] = [];
  const seen = new Set<string>();
  const directions: Array<[Record<string, string[]>, string]> = [
    [controlMappings.NIST_TO_ISO, 'NIST→ISO'],
    [controlMappings.ISO_TO_NIST, 'ISO→NIST'],
  ];
  for (const [table] of directions) {
    for (const [sourceId, targets] of Object.entries(table)) {
      for (const targetId of targets) {
        if (!knownIds.has(sourceId) || !knownIds.has(targetId)) continue;
        const key = `${sourceId}|${targetId}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({
          sourceId,
          targetId,
          type: 'maps-to',
          source: 'controlMapper.js — hand-curated NIST↔ISO crosswalk',
          confidence: 'community',
        });
      }
    }
  }
  return out;
}

const relationships: Relationship[] = mapsToRelationships();

// Fail loud in development on malformed data (build-brief §3, SAD §4.3).
if (process.env.NODE_ENV !== 'production') {
  validateEntities(entities);
  validateRelationships(relationships);
}

export const legacyEntities = entities;
export const legacyRelationships = relationships;
